import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'react-hot-toast'
import { CloseIcon } from '@/components/shared/Icons'

interface Empleado {
  id: string
  nombre: string
  email: string
  activo: boolean
  role_id: string
  role_nombre: string
  nivel: number
  deposito_id: string | null
  deposito_nombre: string | null
  created_at: string
}

function useEmpleados() {
  return useQuery({
    queryKey: ['empleados'],
    queryFn: () => api.get<Empleado[]>('/api/auth/usuarios'),
    staleTime: 30_000,
  })
}

function useToggleEmpleado() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.put(`/api/auth/usuarios/${id}`, { activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empleados'] }),
  })
}

function antiguedad(fechaISO: string): string {
  const desde = new Date(fechaISO)
  const hoy = new Date()
  const dias = Math.floor((hoy.getTime() - desde.getTime()) / 86400000)
  if (dias < 30) return `${dias} día${dias !== 1 ? 's' : ''}`
  const meses = Math.floor(dias / 30)
  if (meses < 12) return `${meses} mes${meses !== 1 ? 'es' : ''}`
  const anios = Math.floor(meses / 12)
  const restaMeses = meses % 12
  return restaMeses > 0 ? `${anios} año${anios !== 1 ? 's' : ''} y ${restaMeses} mes${restaMeses !== 1 ? 'es' : ''}` : `${anios} año${anios !== 1 ? 's' : ''}`
}

const NIVEL_LABELS: Record<number, string> = { 1: 'Administrador', 2: 'Cajero', 3: 'Depósito', 4: 'Solo lectura' }
const NIVEL_PILL: Record<number, string> = { 1: 'me-pill-acid', 2: 'me-pill-ok', 3: 'me-pill-warn', 4: 'me-pill-neutral' }

export default function EmpleadosPage() {
  const { data: empleados = [], isLoading } = useEmpleados()
  const toggle = useToggleEmpleado()
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'inactivos'>('activos')
  const [filtroDeposito, setFiltroDeposito] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [seleccionado, setSeleccionado] = useState<Empleado | null>(null)

  const depositos = [...new Set(empleados.map(e => e.deposito_nombre).filter(Boolean))] as string[]

  const filtrados = empleados.filter(e => {
    if (filtroActivo === 'activos' && !e.activo) return false
    if (filtroActivo === 'inactivos' && e.activo) return false
    if (filtroDeposito && e.deposito_nombre !== filtroDeposito) return false
    if (busqueda && !e.nombre.toLowerCase().includes(busqueda.toLowerCase()) &&
        !e.email?.toLowerCase().includes(busqueda.toLowerCase())) return false
    return true
  })

  async function handleToggle(e: Empleado) {
    try {
      await toggle.mutateAsync({ id: e.id, activo: !e.activo })
      toast.success(e.activo ? 'Empleado desactivado' : 'Empleado activado')
      if (seleccionado?.id === e.id) setSeleccionado({ ...seleccionado, activo: !e.activo })
    } catch { toast.error('Error al actualizar') }
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--me-space-6)' }}>
        <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Administracion</div>
        <h1 className="me-page-title">Empleados</h1>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 'var(--me-space-3)', flexWrap: 'wrap', marginBottom: 'var(--me-space-5)', alignItems: 'center' }}>
        <input
          className="me-input"
          style={{ width: 220 }}
          placeholder="Buscar por nombre o email..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {(['todos', 'activos', 'inactivos'] as const).map(f => (
            <button key={f} onClick={() => setFiltroActivo(f)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: '1px solid var(--me-line)',
                background: filtroActivo === f ? 'var(--me-acid)' : 'var(--me-surface)',
                color: filtroActivo === f ? 'var(--me-acid-ink)' : 'var(--me-text-2)',
                fontFamily: 'var(--me-sans)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                transition: 'background var(--me-dur-short)',
              }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {depositos.length > 0 && (
          <select className="me-input" style={{ width: 180 }}
            value={filtroDeposito} onChange={e => setFiltroDeposito(e.target.value)}>
            <option value="">Todos los depósitos</option>
            {depositos.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--me-text-4)' }}>
          {filtrados.length} empleado{filtrados.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isLoading ? (
        <Skeleton />
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)', fontSize: 13 }}>
          No hay empleados que coincidan con el filtro
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--me-space-4)' }}>
          {filtrados.map(emp => (
            <div
              key={emp.id}
              className="me-card"
              onClick={() => setSeleccionado(emp)}
              style={{
                cursor: 'pointer', opacity: emp.activo ? 1 : 0.55,
                transition: 'box-shadow var(--me-dur-short)',
                userSelect: 'none',
              }}
              onMouseOver={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)')}
              onMouseOut={e => (e.currentTarget.style.boxShadow = '')}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                <Avatar name={emp.nombre} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {emp.nombre}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--me-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {emp.email}
                  </div>
                </div>
                <span className={`me-pill ${NIVEL_PILL[emp.nivel] ?? 'me-pill-neutral'}`}
                  style={emp.nivel === 1 ? { color: '#4a5800' } : undefined}>
                  {emp.role_nombre}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <InfoRow label="Sucursal" value={emp.deposito_nombre ?? 'Sin asignar'} muted={!emp.deposito_nombre} />
                <InfoRow label="Antigüedad" value={antiguedad(emp.created_at)} />
                <InfoRow label="Estado" value={emp.activo ? 'Activo' : 'Inactivo'} ok={emp.activo} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal perfil */}
      {seleccionado && (
        <>
          <div onClick={() => setSeleccionado(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.3)' }} />
          <div className="me-modal-in" style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 41, width: 440, background: 'var(--me-surface)', borderRadius: 16,
            boxShadow: 'var(--me-shadow-modal)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--me-line-2)' }}>
              <h2 className="me-section-title">Perfil del empleado</h2>
              <button onClick={() => setSeleccionado(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', display: 'flex' }}>
                <CloseIcon size={18} />
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <Avatar name={seleccionado.nombre} size={52} />
                <div>
                  <div style={{ fontFamily: 'var(--me-display)', fontVariationSettings: '"opsz" 24, "wght" 700', fontSize: 20 }}>
                    {seleccionado.nombre}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--me-text-3)' }}>{seleccionado.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <ProfileRow label="Rol" value={seleccionado.role_nombre} badge nivel={seleccionado.nivel} />
                <ProfileRow label="Nivel de acceso" value={NIVEL_LABELS[seleccionado.nivel] ?? `Nivel ${seleccionado.nivel}`} />
                <ProfileRow label="Sucursal / Depósito" value={seleccionado.deposito_nombre ?? 'Sin asignar'} />
                <ProfileRow label="Ingresó el" value={new Date(seleccionado.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })} />
                <ProfileRow label="Antigüedad" value={antiguedad(seleccionado.created_at)} />
                <ProfileRow label="Estado" value={seleccionado.activo ? 'Activo' : 'Inactivo'} />
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--me-line-2)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="me-btn me-btn-ghost" onClick={() => setSeleccionado(null)}>Cerrar</button>
              <button
                className={`me-btn ${seleccionado.activo ? 'me-btn-danger' : 'me-btn-primary'}`}
                onClick={() => handleToggle(seleccionado)}
                disabled={toggle.isPending}
              >
                {toggle.isPending ? 'Procesando...' : seleccionado.activo ? 'Desactivar' : 'Activar'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28, flexShrink: 0,
      background: 'var(--me-acid)', color: 'var(--me-acid-ink)',
      fontFamily: 'var(--me-display)', fontWeight: 700, fontSize: size * 0.38,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{initials}</div>
  )
}

function InfoRow({ label, value, muted, ok }: { label: string; value: string; muted?: boolean; ok?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ color: 'var(--me-text-4)' }}>{label}</span>
      <span style={{ color: muted ? 'var(--me-text-4)' : ok === false ? 'var(--me-bad)' : ok === true ? 'var(--me-ok)' : 'var(--me-text-2)', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

function ProfileRow({ label, value, badge, nivel }: { label: string; value: string; badge?: boolean; nivel?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--me-line-2)' }}>
      <span style={{ fontSize: 13, color: 'var(--me-text-3)' }}>{label}</span>
      {badge && nivel !== undefined ? (
        <span className={`me-pill ${NIVEL_PILL[nivel] ?? 'me-pill-neutral'}`} style={nivel === 1 ? { color: '#4a5800' } : undefined}>
          {value}
        </span>
      ) : (
        <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
      )}
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ height: 130, background: 'var(--me-surface-3)', borderRadius: 12, opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  )
}
